import { useCallback, useEffect, useMemo, useState } from "react"

import { useMutation } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import {
  Accordion,
  Button,
  Divider,
  Form,
  Grid,
  Icon,
  Input,
  Message,
  Modal,
  Segment,
  type SemanticICONS,
} from "semantic-ui-react"
// @ts-ignore
import { languages, setInterpolationDictionary } from "simplydo/core/i18n"
import type { OpenAPI } from "simplydo/interfaces"
import styled from "styled-components"

import actions from "actions"
import api, { asMutation } from "api"
import { ColourChooser, FileChooser } from "components/lib/Choosers"
import { DndContext } from "components/lib/dnd/DndContext"
import { Draggable } from "components/lib/dnd/Draggable"
import { Droppable } from "components/lib/dnd/Droppable"
import { moveItem } from "components/lib/dnd/dndHelpers"
import SettingActions from "components/lib/SettingActions"
import { useAppDispatch, useAppSelector } from "store"
import constants from "utils/constants"
import Prompt from "utils/Prompt"
import util from "utils/utils"

import { SegmentContainer } from "./General"

const CapitalisedInput = styled(Form.Input)`
  input {
    text-transform: capitalize;
  }
`

type OrgNavBarItem = OpenAPI.Schemas["Organisation"]["navbar"][number] & {
  translate?: string
  title?: string
  location?: string
  external?: boolean
  permissions?: string[]
  icon?: string
}
type OrgNavBar = Array<OrgNavBarItem>

const defaultOrgNavBarItems: OrgNavBar = [
  { id: "home", translate: "generic.home", location: "/", icon: "home" },
  { id: "challenges", translate: "generic.challenges", location: "challenges", icon: "target" },
  { id: "ideas", translate: "generic.ideas", location: "ideas", icon: "lightbulb" },
  { id: "groups", translate: "generic.groups", location: "groups", icon: "users" },
  { id: "admin", translate: "generic.admin", location: "admin", permissions: ["org.viewDashboard"], icon: "cogs" },
  {
    id: "innovationintelligence",
    translate: "innointel.title",
    location: "innovationintelligence",
    permissions: ["org.innovationIntelligence"],
    icon: "building",
  },
  {
    id: "innointel2",
    translate: "innointel.localSearchTitle",
    location: "innointel2",
    permissions: ["org.innovationIntelligence"],
    icon: "building",
  },
]

export const useOrgNavBar = () => {
  const user = useAppSelector((state) => state.user)
  const orgHasCustomNavBar = !!user?.ownerOrganisation?.navbar?.length
  const orgId = user?.ownerOrganisation?._id

  const innointelEnabled = useMemo(
    () => user?.ownerOrganisation?.enabledFeatures?.includes("innovationIntelligence"),
    [user],
  )
  const localSearchEnabled = useMemo(
    () => innointelEnabled && user?.ownerOrganisation?.enabledFeatures?.includes("innovationIntelligenceLocalSearch"),
    [user, innointelEnabled],
  )

  const visibleDefaultItems = useMemo(
    () =>
      defaultOrgNavBarItems.filter((item) => {
        if (item.id === "innovationintelligence" && !innointelEnabled) {
          return false
        }
        if (item.id === "innointel2" && !localSearchEnabled) {
          return false
        }
        return true
      }),
    [innointelEnabled, localSearchEnabled],
  )

  const allItems: OrgNavBarItem[] = useMemo(
    () =>
      visibleDefaultItems
        .filter((item) => {
          if (item.id === "innovationintelligence" && !innointelEnabled) {
            return false
          }
          if (item.id === "innointel2" && !localSearchEnabled) {
            return false
          }
          return true
        })
        .concat(
          (user?.ownerOrganisation?.links || []).map((link) => ({
            ...link,
            type: "link",
            icon: "chain",
          })) || [],
        ),
    [visibleDefaultItems, user?.ownerOrganisation?.links, innointelEnabled, localSearchEnabled],
  )

  const entries: OrgNavBarItem[] = useMemo(() => {
    const navbarItems: OrgNavBarItem[] = orgHasCustomNavBar
      ? user?.ownerOrganisation?.navbar?.map((item) => {
          if (item.type === "link") {
            const fullLink = allItems.find((link) => link.id === item.id)
            if (fullLink) {
              return fullLink
            }
          }
          const defaultItem = visibleDefaultItems.find((defaultEntry) => defaultEntry.id === item.id)
          if (defaultItem) {
            return defaultItem
          }
          // fallback for custom links that have been removed from org settings but are still in navbar
          return item
        }) || []
      : visibleDefaultItems

    return navbarItems.filter((entry) => {
      if (!entry.permissions) {
        return true
      }
      return !!entry.permissions.find((permission) => util.hasPermission(user, permission, orgId))
    })
  }, [visibleDefaultItems, user, allItems, orgHasCustomNavBar, orgId])

  return {
    entries,
    allItems,
  }
}

const NavBarEntry = ({
  item,
  setEditingLinkModalOpen,
  setEditingLink,
  deleteCustomLink,
}: {
  item: OrgNavBarItem
  setEditingLinkModalOpen: (open: boolean) => void
  setEditingLink: (link: OpenAPI.Schemas["Organisation"]["links"][number]) => void
  deleteCustomLink: (linkId: string) => void
}) => {
  const { t } = useTranslation()
  const permissions = (item.permissions || [])
    .map((permission) => t(constants.permissions.org.find((p) => p.key === permission)?.title))
    .join(", ")
  return (
    <Segment style={{ display: "flex", alignItems: "center", padding: "0.6em 1em", minHeight: "45px", gap: 4 }}>
      <Icon name={item.icon as SemanticICONS} style={{ height: "1lh" }} />
      <div style={{ flex: 1 }}>
        {item.translate ? t("common:capitalise", { key: item.translate }) : item.title}
        <p style={{ fontSize: "0.7em", textTransform: "none" }}>
          {item.type === "page" ? "Custom page" : item.type === "link" ? "Custom link:  " : "Default entry"}
          {permissions ? `. Visibility restricted to users with permissions: ${permissions}` : ""}
          {item.type === "link" && `${item.location}`}
        </p>
      </div>
      {item.type === "link" && (
        <>
          <Button icon="trash" size="mini" onClick={() => deleteCustomLink(item.id)} />
          <Button
            size="mini"
            icon="edit"
            onClick={() => {
              setEditingLinkModalOpen(true)
              // @ts-ignore
              setEditingLink(item)
            }}
            content="Edit"
          />
        </>
      )}
    </Segment>
  )
}

const SettingsBranding = () => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)
  const dispatch = useAppDispatch()

  const { entries, allItems } = useOrgNavBar()

  const [visibleEntries, setVisibleEntries] = useState(entries)
  const [hiddenEntries, setHiddenEntries] = useState<OrgNavBar>([])
  const [navBarUnsaved, setNavBarUnsaved] = useState(false)

  useEffect(() => {
    setVisibleEntries(entries)
  }, [entries])

  useEffect(() => {
    setHiddenEntries(allItems.filter((item) => !visibleEntries.find((enabledItem) => enabledItem.id === item.id)))
  }, [visibleEntries, allItems])

  const [newTitle, setNewTitle] = useState(user?.ownerOrganisation?.appName)
  const [newWelcomeText, setNewWelcomeText] = useState(user?.ownerOrganisation?.welcomeText)
  const [newDashboardText, setNewDashboardText] = useState(user?.ownerOrganisation?.dashboardText)
  const [editingLinkModalOpen, setEditingLinkModalOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<OpenAPI.Schemas["Organisation"]["links"][number]>({
    type: "link",
  })

  const [newTerminology, setNewTerminology] = useState(user?.ownerOrganisation?.terminology ?? {})
  const [terminologyTranslationOpen, setTerminologyTranslationOpen] = useState<{
    challenge?: boolean
    idea?: boolean
    impact?: boolean
  }>({})

  const onOrganisationEdited = useCallback(
    (field, value) => {
      dispatch(actions.user.updateOrganisation(field, value))
    },
    [dispatch],
  )

  const updateOrgMutation = useMutation({
    mutationFn: asMutation(api.organisations.updateSettings)!,
    onSuccess: ({ navbar, links }) => {
      api.queryClient.setQueryData(["organisations", user.ownerOrganisation._id], (oldData: any) => ({
        ...oldData,
        navbar,
        links,
      }))
      onOrganisationEdited("links", links)
      onOrganisationEdited("navbar", navbar)
    },
  })

  const toDatabase = useCallback(
    (entries: OrgNavBar) =>
      entries.map((item) =>
        item.type
          ? {
              id: item.id,
              type: item.type,
            }
          : { id: item.id },
      ),
    [],
  )

  const updateOrgLinks = useCallback(() => {
    const orgLinks = user.ownerOrganisation?.links || []
    const existingLink = orgLinks.find((link) => link.id === editingLink?.id)
    // This is containing the new but not the old entry
    const updatedLinks = [...orgLinks]
    const updatedNavbar = [...visibleEntries]

    if (existingLink) {
      const updatedLinksIndex = orgLinks.findIndex((link) => link.id === existingLink.id)
      updatedLinks[updatedLinksIndex] = {
        ...existingLink,
        title: editingLink.title,
        location: editingLink.location,
        external: editingLink.external,
      }

      // if the link is in sidebar - that also need to be updated
      const navbarIndex = visibleEntries.findIndex((link) => link.id === existingLink.id)
      if (navbarIndex !== -1) {
        updatedNavbar[navbarIndex] = {
          ...updatedNavbar[navbarIndex],
          title: editingLink.title,
          location: editingLink.location,
          external: editingLink.external,
        }
        setVisibleEntries(updatedNavbar)
      }
    } else {
      updatedLinks.push({
        ...editingLink,
      })
    }

    // Org Mutation
    updateOrgMutation.mutate({
      params: [
        user.ownerOrganisation._id,
        {
          links: updatedLinks,
          navbar: toDatabase(updatedNavbar),
        },
      ],
    })
    setEditingLinkModalOpen(false)
  }, [editingLink, visibleEntries, user.ownerOrganisation, updateOrgMutation, toDatabase])

  const deleteCustomLink = useCallback(
    (linkId: string) => {
      const updatedLinks = user.ownerOrganisation.links.filter((link) => link.id !== linkId)
      const updatedNavbar = visibleEntries.filter((link) => link.id !== linkId)

      setVisibleEntries(updatedNavbar)

      updateOrgMutation.mutate({
        params: [
          user.ownerOrganisation._id,
          {
            links: updatedLinks,
            navbar: toDatabase(updatedNavbar),
          },
        ],
      })
    },
    [user.ownerOrganisation, updateOrgMutation, visibleEntries, toDatabase],
  )

  const saveOrg = useCallback(
    (field: string, value?: any, hideToast?: boolean) => {
      const org = user.ownerOrganisation
      const currentValue = org[field]
      if (currentValue !== value) {
        onOrganisationEdited(field, value)
      }
      api.organisations.updateSettings(
        org._id,
        { [field]: value },
        () => {
          if (!hideToast) {
            toast.success(t("generic.typeUpdated", { type: "Settings" }))
          }
        },
        (err) => {
          toast.error(err.message)
          onOrganisationEdited(field, currentValue)
        },
      )
    },
    [onOrganisationEdited, user.ownerOrganisation, t],
  )

  const saveLogo = useCallback(
    (logo) => {
      api.organisations.updateSettings(
        user.ownerOrganisation._id,
        { darkLogo: logo },
        (organisation) => {
          toast.success(t("generic.typeUpdated", { type: "Settings" }))
          onOrganisationEdited("darkLogo", logo)
          onOrganisationEdited("darkLogoUrl", organisation.darkLogoUrl)
        },
        (err) => toast.error(err.message),
      )
    },
    [onOrganisationEdited, t, user.ownerOrganisation._id],
  )

  const saveFavicon = useCallback(
    (favicon, _name?: string, faviconUrl?: string) => {
      api.organisations.updateSettings(
        user.ownerOrganisation._id,
        { favicon },
        () => {
          toast.success(t("generic.typeUpdated", { type: "Settings" }))
          onOrganisationEdited("favicon", favicon)
          onOrganisationEdited("faviconUrl", faviconUrl)
        },
        (err) => toast.error(err.message),
      )
    },
    [onOrganisationEdited, t, user.ownerOrganisation._id],
  )

  const swapColours = useCallback(() => {
    const { primaryColour, secondaryColour } = user.ownerOrganisation
    saveOrg("primaryColour", secondaryColour, true)
    saveOrg("secondaryColour", primaryColour, true)
  }, [saveOrg, user.ownerOrganisation])

  const setColour = useCallback(
    (type, colour) => {
      saveOrg(type, colour, true)
    },
    [saveOrg],
  )

  const changes: any = {}
  if (newTitle !== user.ownerOrganisation.appName) {
    changes.appName = newTitle
  }
  if (newWelcomeText !== user.ownerOrganisation.welcomeText) {
    changes.welcomeText = newWelcomeText
  }
  if (newDashboardText !== user.ownerOrganisation.dashboardText) {
    changes.dashboardText = newDashboardText
  }
  const changed = Object.keys(changes).length > 0

  return (
    <div>
      <Prompt when={changed} message={"You have unsaved changes. Are you sure you want to leave?"} />
      {changed ? (
        <Message info>
          <Message.Header>You have unsaved changes.</Message.Header>
          <p>Before leaving this page make sure to save your changes.</p>
          <Button
            primary
            content="Save now"
            size="tiny"
            onClick={() => {
              Object.entries(changes).forEach(([key, value], i, entries) => {
                saveOrg(key, value as string, i === entries.length - 1)
              })
            }}
          />
        </Message>
      ) : null}
      <SegmentContainer>
        <Segment style={{ zIndex: "auto" }}>
          <Grid stackable>
            <Grid.Column computer={8}>
              <h3>{t("admin.settings.branding.logo.title")}</h3>
              <p>{t("admin.settings.branding.logo.info", { user })}</p>
              <FileChooser
                forType="organisation"
                forId={user.ownerOrganisation._id}
                trigger={<Button floated="right" primary content={t("admin.settings.branding.logo.button")} fluid />}
                onComplete={saveLogo}
              />
            </Grid.Column>
            <Grid.Column computer={8}>
              <img
                alt="Organisation logo"
                src={user.ownerOrganisation.darkLogoUrl}
                style={{ maxWidth: "100%", display: "block", margin: "10px auto" }}
              />
            </Grid.Column>
          </Grid>
        </Segment>

        <Segment>
          <h3>{t("admin.settings.branding.colourScheme.title")}</h3>
          <p>{t("admin.settings.branding.colourScheme.info")}</p>
          <Grid stackable columns={2} style={{ marginTop: 10 }}>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <h4>{t("admin.settings.branding.colourScheme.primary")}</h4>
                <div style={{ width: "220px", display: "block", margin: "10px auto" }}>
                  <ColourChooser
                    value={user.ownerOrganisation.primaryColour}
                    onChange={(c) => setColour("primaryColour", c)}
                  />
                </div>
              </Grid.Column>
              <Grid.Column textAlign="center">
                <h4>{t("admin.settings.branding.colourScheme.secondary")}</h4>
                <div style={{ width: "220px", display: "block", margin: "10px auto" }}>
                  <ColourChooser
                    value={user.ownerOrganisation.secondaryColour}
                    onChange={(c) => setColour("secondaryColour", c)}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 10,
            }}
          >
            <Button primary icon="shuffle" content="Swap colours" onClick={swapColours} />
          </div>
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>{t("admin.settings.branding.browserTitle.title")}</h3>
          <p>{t("admin.settings.branding.browserTitle.info")}</p>
          <Input
            style={{ width: "100%" }}
            placeholder={newTitle || util.appName()}
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value)
            }}
            action={
              <Button
                floated="right"
                content="Save name"
                primary
                disabled={newTitle === user?.ownerOrganisation?.appName}
                onClick={() => {
                  saveOrg("appName", newTitle)
                }}
              />
            }
          />
        </Segment>

        <Segment>
          <h3>{t("admin.settings.branding.favicon.title")}</h3>
          <p>{t("admin.settings.branding.favicon.info")}</p>
          <img
            alt="Favicon"
            src={util.orgFavicon(user.ownerOrganisation)}
            style={{ maxWidth: "25%", display: "block", margin: "10px auto" }}
          />
          {user.ownerOrganisation.faviconUrl && (
            <Button
              floated="right"
              content="Remove"
              onClick={() => {
                saveFavicon("")
              }}
            />
          )}
          <div style={{ float: "right" }}>
            <FileChooser
              forType="organisation"
              forId={user.ownerOrganisation._id}
              trigger={<Button floated="right" primary content={t("admin.settings.branding.logo.button")} />}
              onComplete={saveFavicon}
            />
          </div>
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>{t("admin.settings.branding.welcomeText.title")}</h3>
          <p>{t("admin.settings.branding.welcomeText.info")}</p>
          <Form>
            <Form.TextArea
              placeholder={t("admin.settings.branding.welcomeText.placeholder")}
              value={newWelcomeText}
              onChange={(e) => {
                setNewWelcomeText(e.target.value)
              }}
            />
          </Form>
          <Button
            floated="right"
            content={t("generic.save")}
            primary
            disabled={user.ownerOrganisation.welcomeText === newWelcomeText}
            onClick={() => {
              saveOrg("welcomeText", newWelcomeText)
            }}
            style={{ marginTop: 16 }}
          />
        </Segment>

        <Segment>
          <h3>{t("admin.settings.branding.homepageText.title")}</h3>
          <p>{t("admin.settings.branding.homepageText.info")}</p>
          <Form>
            <Form.TextArea
              placeholder={t("admin.settings.branding.homepageText.placeholder")}
              value={newDashboardText}
              onChange={(e) => {
                setNewDashboardText(e.target.value)
              }}
            />
          </Form>
          <Button
            floated="right"
            content={t("generic.save")}
            primary
            disabled={user.ownerOrganisation.dashboardText === newDashboardText}
            onClick={() => {
              saveOrg("dashboardText", newDashboardText)
            }}
            style={{ marginTop: 16 }}
          />
        </Segment>
      </SegmentContainer>
      <SegmentContainer>
        <Segment>
          <h3>{t("admin.settings.branding.terminology.title")}</h3>
          <p>{t("admin.settings.branding.terminology.info")}</p>

          <Form>
            <CapitalisedInput
              label={t("admin.settings.branding.terminology.challenge")}
              placeholder="Challenge"
              value={newTerminology?.en?.challenge}
              onChange={(_e, { value }) => {
                setNewTerminology((prev) => {
                  // map for each language and update
                  return languages.reduce((acc, lang) => {
                    acc[lang.key] = {
                      ...(prev[lang.key] ?? {}),
                      challenge: value.toLowerCase(),
                      challenges: value
                        ? t("generic.challengesDefault", {
                            lng: "en",
                            "generic.challenge": value.toLowerCase(),
                          })
                        : "",
                    }
                    return acc
                  }, {})
                })
              }}
            />
            <Accordion fluid styled>
              <Accordion.Title
                active={terminologyTranslationOpen?.challenge}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    challenge: !prev.challenge,
                  }))
                }
              >
                <Icon name="dropdown" />
                {t("admin.settings.branding.terminology.translations")}
              </Accordion.Title>
              <Accordion.Content active={terminologyTranslationOpen?.challenge}>
                {languages.map((lang) => (
                  <>
                    <Form.Group key={lang.key} widths="equal">
                      <CapitalisedInput
                        label={lang.name}
                        placeholder={`Default: ${t("generic.challengeDefault", { lng: lang.key })}`}
                        value={newTerminology?.[lang.key]?.challenge}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), challenge: value.toLowerCase() },
                          }))
                        }}
                      />
                      <CapitalisedInput
                        label={`${lang.name} (Plural)`}
                        disabled={!newTerminology?.[lang.key]?.challenge && !newTerminology?.[lang.key]?.challenges}
                        placeholder={`Default: ${t("generic.challengesDefault", { lng: lang.key, "generic.challenge": newTerminology?.[lang.key]?.challenge || "challenge" })}`}
                        value={newTerminology?.[lang.key]?.challenges}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), challenges: value.toLowerCase() },
                          }))
                        }}
                      />
                    </Form.Group>
                    <Divider hidden />
                  </>
                ))}
              </Accordion.Content>
            </Accordion>
          </Form>
          <Divider />
          <Form>
            <CapitalisedInput
              label={t("admin.settings.branding.terminology.idea")}
              placeholder={`${t("common:capitalise", { key: "generic.idea" })}`}
              value={newTerminology?.en?.idea}
              onChange={(_e, { value }) => {
                setNewTerminology((prev) => {
                  // map for each language and update
                  return languages.reduce((acc, lang) => {
                    acc[lang.key] = {
                      ...(prev[lang.key] ?? {}),
                      idea: value.toLowerCase(),
                      ideas: value
                        ? t("generic.ideasDefault", {
                            lng: "en",
                            "generic.idea": value.toLowerCase(),
                          })
                        : "",
                    }
                    return acc
                  }, {})
                })
              }}
            />
            <Accordion fluid styled>
              <Accordion.Title
                active={terminologyTranslationOpen?.idea}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    idea: !prev.idea,
                  }))
                }
              >
                <Icon name="dropdown" />
                {t("admin.settings.branding.terminology.translations")}
              </Accordion.Title>
              <Accordion.Content active={terminologyTranslationOpen?.idea}>
                {languages.map((lang) => (
                  <>
                    <Form.Group key={lang.key} widths="equal">
                      <CapitalisedInput
                        label={lang.name}
                        placeholder={`Default: ${t("generic.ideaDefault", { lng: lang.key })}`}
                        value={newTerminology?.[lang.key]?.idea}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), idea: value.toLowerCase() },
                          }))
                        }}
                      />
                      <CapitalisedInput
                        label={`${lang.name} (Plural)`}
                        disabled={!newTerminology?.[lang.key]?.ideas}
                        placeholder={`Default: ${t("generic.ideas", { lng: lang.key, "generic.idea": newTerminology?.[lang.key]?.idea || "idea" })}`}
                        value={newTerminology?.[lang.key]?.ideas}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), ideas: value.toLowerCase() },
                          }))
                        }}
                      />
                    </Form.Group>
                    <Divider hidden />
                  </>
                ))}
              </Accordion.Content>
            </Accordion>
          </Form>
          <Divider />
          <Form>
            <CapitalisedInput
              label={`Rename 'Impact' to:`}
              placeholder={`"${t("common:capitalise", { key: "generic.impact" })}"`}
              value={newTerminology?.en?.impact}
              onChange={(_e, { value }) => {
                setNewTerminology((prev) => {
                  // map for each language and update
                  return languages.reduce((acc, lang) => {
                    acc[lang.key] = {
                      ...(prev[lang.key] ?? {}),
                      impact: value.toLowerCase(),
                      impacts: value
                        ? t("generic.impactsDefault", {
                            lng: "en",
                            "generic.impact": value.toLowerCase(),
                          })
                        : "",
                    }
                    return acc
                  }, {})
                })
              }}
            />
            <Accordion fluid styled>
              <Accordion.Title
                active={terminologyTranslationOpen?.impact}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    impact: !prev.impact,
                  }))
                }
              >
                <Icon name="dropdown" />
                {t("admin.settings.branding.terminology.translations")}
              </Accordion.Title>
              <Accordion.Content active={terminologyTranslationOpen?.impact}>
                {languages.map((lang) => (
                  <>
                    <Form.Group key={lang.key} widths="equal">
                      <CapitalisedInput
                        label={lang.name}
                        placeholder={`Default: ${t("generic.impactDefault", { lng: lang.key })}`}
                        value={newTerminology?.[lang.key]?.impact}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), impact: value.toLowerCase() },
                          }))
                        }}
                      />
                      <CapitalisedInput
                        label={`${lang.name} (Plural)`}
                        disabled={!newTerminology?.[lang.key]?.impacts}
                        placeholder={`Default: ${t("generic.impacts", { lng: lang.key, "generic.idea": newTerminology?.[lang.key]?.idea || "idea" })}`}
                        value={newTerminology?.[lang.key]?.impacts}
                        onChange={(_e, { value }) => {
                          setNewTerminology((prev) => ({
                            ...prev,
                            [lang.key]: { ...(prev[lang.key] ?? {}), impacts: value.toLowerCase() },
                          }))
                        }}
                      />
                    </Form.Group>
                    <Divider hidden />
                  </>
                ))}
              </Accordion.Content>
            </Accordion>
          </Form>
          <SettingActions>
            <Button
              primary
              content={t("generic.save")}
              onClick={() => {
                saveOrg("terminology", newTerminology)
                setInterpolationDictionary(newTerminology)
              }}
            />
          </SettingActions>
        </Segment>
        <Segment style={{ zIndex: "auto" }}>
          <Grid stackable>
            <Grid.Column computer={16}>
              <h3>Navigation Bar</h3>
              <p>
                You can change the order of the navigation bar items by dragging them. The first entry in this list will
                be the first page users see when loading into the platform.
              </p>
              <Message
                size="tiny"
                info
                content={
                  "The top five visible entries will be displayed directly on the navigation bar, remaining visible entires will be accessible via the 'More' dropdown."
                }
              ></Message>
              <Button
                primary
                onClick={() => {
                  saveOrg("navbar", toDatabase(visibleEntries))
                  setNavBarUnsaved(false)
                }}
                size="tiny"
                disabled={!navBarUnsaved}
              >
                Save changes
              </Button>
              <Button
                onClick={() => {
                  saveOrg("navbar", null)
                  setNavBarUnsaved(false)
                }}
                size="small"
              >
                <Icon name="redo" />
                Reset
              </Button>
              <Divider />
              <DndContext
                onDragOver={(event) => {
                  const moveResult = moveItem(
                    event,
                    {
                      "enabled-navbar": { items: visibleEntries, statefulSetter: setVisibleEntries },
                      "disabled-navbar": { items: hiddenEntries, statefulSetter: setHiddenEntries },
                    },
                    {
                      restrictMovesTo: "differentDroppableOnly",
                      canMoveItem: ({ item, toDroppable }) => {
                        if (item.permissions && toDroppable === "disabled-navbar") {
                          return false
                        }
                        return true
                      },
                    },
                  )
                  if (moveResult.didMove) {
                    setNavBarUnsaved(true)
                  }
                }}
                onDragEnd={(event) => {
                  const moveResult = moveItem(event, {
                    "enabled-navbar": { items: visibleEntries, statefulSetter: setVisibleEntries },
                    "disabled-navbar": { items: hiddenEntries, statefulSetter: setHiddenEntries },
                  })
                  if (moveResult.didMove) {
                    setNavBarUnsaved(true)
                  }
                }}
              >
                <h4>Visible entries</h4>
                <Droppable id="enabled-navbar" itemIds={visibleEntries.map((item) => item.id)}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {visibleEntries.map((item) => (
                      <Draggable key={item.id} id={item.id}>
                        <NavBarEntry
                          setEditingLink={setEditingLink}
                          setEditingLinkModalOpen={setEditingLinkModalOpen}
                          deleteCustomLink={deleteCustomLink}
                          key={item.id}
                          item={item}
                        />
                      </Draggable>
                    ))}
                  </div>
                </Droppable>
                <h4>Hidden entries</h4>
                <Droppable id="disabled-navbar" itemIds={hiddenEntries.map((item) => item.id)}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {hiddenEntries.length === 0 && <p>There are no hidden entries</p>}
                    {hiddenEntries.map((item) => (
                      <Draggable key={item.id} id={item.id}>
                        <NavBarEntry
                          setEditingLink={setEditingLink}
                          setEditingLinkModalOpen={setEditingLinkModalOpen}
                          deleteCustomLink={deleteCustomLink}
                          key={item.id}
                          item={item}
                        />
                      </Draggable>
                    ))}
                  </div>
                </Droppable>
              </DndContext>
              <Divider />
              <h4>Custom links</h4>
              <p>
                Add custom links to the end of the navigation bar, for example to link to an external blog or website.
              </p>
              <Button onClick={() => setEditingLinkModalOpen(true)} size="small">
                <Icon name="plus" />
                Add custom navigation bar link
              </Button>
            </Grid.Column>
          </Grid>
        </Segment>
      </SegmentContainer>
      <Modal
        open={editingLinkModalOpen}
        onClose={() => setEditingLinkModalOpen(false)}
        mountNode={document.getElementById("semantic-modal-mount-node")}
      >
        <Modal.Header>Custom navigation bar link</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <p>Add a navigation bar link to direct users to additional content.</p>
            <p>
              <strong>Note:</strong> This link will not be visible to members of your organisation until you add it to
              the navigation bar.
            </p>
            <Form>
              <Form.Field>
                <label>Title</label>
                <Input
                  type="text"
                  placeholder="My custom link"
                  value={editingLink?.title}
                  onChange={(e) => setEditingLink({ ...editingLink, title: e.target.value })}
                />
              </Form.Field>
              <Form.Field>
                <label>Link</label>
                <Input
                  type="text"
                  placeholder="https://example.com"
                  value={editingLink?.location}
                  onChange={(e) => setEditingLink({ ...editingLink, location: e.target.value })}
                />
              </Form.Field>
              <Form.Checkbox
                style={{ marginTop: 10 }}
                label="Open link in new tab"
                checked={editingLink?.external}
                onChange={(_e, { checked }) => setEditingLink({ ...editingLink, external: checked })}
              />
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setEditingLinkModalOpen(false)
              setEditingLink({ type: "link" })
            }}
            content="Cancel"
          />
          <Button
            primary
            onClick={() => {
              updateOrgLinks()
              setEditingLink({ type: "link" })
            }}
            // disabled={!editingLink?.location || !editingLink?.title}
            icon="checkmark"
            content="Save"
          />
        </Modal.Actions>
      </Modal>
    </div>
  )
}

export default SettingsBranding
